import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import { awaitOpen, type DeleteOutcome, deleteDatabaseBounded } from "@/store/persistence/idbConnection";
import { workspaceId as activeWorkspaceId, localDataVersion } from "@/store/scope";
import { retryWithBackoff } from "@/store/storageRetry";
import { isQuotaExceededError } from "@/utils/localStorage";

export type BootstrapEventMetadata = {
  name: string;
  tableName?: string;
  initialBootstrapTime?: string; // ISO string
  latestBootstrapTime?: string; // ISO string (last insertBootstrapEvent call)
  mostRecentUpdateTime?: string; // ISO string (last touch/update)
  /** Opaque to the registry: the sync that owns the event serializes its own state here, e.g. a log's coverage. */
  state?: string;
};

type WorkspaceRegistryRecord = {
  workspaceId: string;
  events: Record<string, BootstrapEventMetadata>;
  version: number;
};

/**
 * Latest record per workspace, mirrored in memory on every read/write so the snapshot persister
 * can capture cursor state synchronously and atomically with the rows it writes.
 */
const recordCache = new Map<string, WorkspaceRegistryRecord>();

export const IDB_DB_NAME = `${localDataVersion}-sbweb-bootstrap-registry`;
const IDB_STORE_NAME = "workspaces";
const IDB_VERSION = 1;

async function openRegistryDb(): Promise<IDBDatabase> {
  return await retryWithBackoff(
    () =>
      awaitOpen(window.indexedDB.open(IDB_DB_NAME, IDB_VERSION), IDB_DB_NAME, (db) => {
        if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
          db.createObjectStore(IDB_STORE_NAME, { keyPath: "workspaceId" });
        }
      }),
    {
      maxRetries: 3,
      initialDelayMs: 500,
      label: "openRegistryDb",
      // QuotaExceededError won't recover by retrying - fail fast.
      shouldRetry: (e) => !isQuotaExceededError(e),
    },
  );
}

async function readWorkspaceRecord(db: IDBDatabase, workspaceId: string): Promise<WorkspaceRegistryRecord> {
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE_NAME, "readonly");
    const store = tx.objectStore(IDB_STORE_NAME);
    const getReq = store.get(workspaceId);
    getReq.onsuccess = () => {
      const existing: WorkspaceRegistryRecord | undefined = getReq.result as WorkspaceRegistryRecord | undefined;
      const record = existing ?? { workspaceId, events: {}, version: IDB_VERSION };
      recordCache.set(workspaceId, record);
      resolve(record);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

async function writeWorkspaceRecord(db: IDBDatabase, record: WorkspaceRegistryRecord): Promise<void> {
  recordCache.set(record.workspaceId, record);
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    const putReq = store.put(record);
    putReq.onsuccess = () => resolve();
    putReq.onerror = () => {
      if (isQuotaExceededError(putReq.error)) {
        log.warn("[BOOTSTRAP][REGISTRY] QuotaExceededError writing workspace record, skipping persist");
        resolve();
        return;
      }
      reject(putReq.error);
    };
    tx.onabort = () => {
      if (isQuotaExceededError(tx.error)) {
        log.warn("[BOOTSTRAP][REGISTRY] QuotaExceededError on transaction abort, skipping persist");
        resolve();
        return;
      }
      reject(tx.error ?? new DOMException("Transaction aborted", "AbortError"));
    };
  });
}

/**
 * Reads, changes and writes one workspace's record inside a single readwrite transaction.
 *
 * IndexedDB runs readwrite transactions on a store one at a time, across connections, so concurrent updates each see
 * the one before; a read and a write in separate transactions let every caller put back a record missing the others'
 * changes. A quota failure skips the write, as {@link writeWorkspaceRecord} does.
 *
 * @param mutate - Changes the record in place, and returns what the caller wants back.
 */
async function updateWorkspaceRecord<T>(
  db: IDBDatabase,
  workspaceId: string,
  mutate: (record: WorkspaceRegistryRecord) => T,
): Promise<T> {
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    let result: T;
    const getReq = store.get(workspaceId);
    getReq.onsuccess = () => {
      const existing = getReq.result as WorkspaceRegistryRecord | undefined;
      const record = existing ?? { workspaceId, events: {}, version: IDB_VERSION };
      result = mutate(record);
      recordCache.set(workspaceId, record);
      store.put(record);
    };
    getReq.onerror = () => reject(getReq.error);
    tx.oncomplete = () => resolve(result);
    tx.onabort = () => {
      if (isQuotaExceededError(tx.error)) {
        log.warn("[BOOTSTRAP][REGISTRY] QuotaExceededError on transaction abort, skipping persist");
        resolve(result);
        return;
      }
      reject(tx.error ?? new DOMException("Transaction aborted", "AbortError"));
    };
  });
}

export class BootstrapEventsRegistry {
  private static instance: BootstrapEventsRegistry | null = null;

  static getInstance(): BootstrapEventsRegistry {
    if (!BootstrapEventsRegistry.instance) {
      BootstrapEventsRegistry.instance = new BootstrapEventsRegistry();
    }
    return BootstrapEventsRegistry.instance;
  }

  private constructor() {}

  /**
   * The event's cursor, or `undefined` when there is none *or* the registry is unreadable. The
   * guard wraps `withDb` rather than sitting inside its callback: `openRegistryDb` throws before
   * the callback ever runs (slow open, blocked by another tab), and a cursor read is the least
   * important thing on the boot path — callers degrade to a timestamp-less full fetch instead of
   * rejecting into an unhandled bootstrap failure (WEB-2245).
   */
  public async getLatestEventDate(name: string, workspaceId?: string): Promise<Date | undefined> {
    const wsId = this.normalizeWorkspaceId(workspaceId);
    try {
      return await this.withDb(async (db: IDBDatabase): Promise<Date | undefined> => {
        const record = await readWorkspaceRecord(db, wsId);
        const metadata = record.events[name];
        const iso = metadata?.mostRecentUpdateTime ?? metadata?.latestBootstrapTime;
        return iso ? new Date(iso) : undefined;
      });
    } catch (e) {
      log.warn("[BOOTSTRAP][REGISTRY] getLatestEventDate failed", { name, workspaceId: wsId, error: e });
      return undefined;
    }
  }

  public async markBootstrap(
    name: string,
    tableName?: string,
    workspaceId?: string,
    recordedAt?: Date,
  ): Promise<BootstrapEventMetadata> {
    const nowIso = (recordedAt ?? new Date()).toISOString();
    return await this.withDb(
      async (db: IDBDatabase): Promise<BootstrapEventMetadata> =>
        await updateWorkspaceRecord(db, this.normalizeWorkspaceId(workspaceId), (record) => {
          const metadata: BootstrapEventMetadata = {
            name,
            tableName,
            initialBootstrapTime: record.events[name]?.initialBootstrapTime ?? nowIso,
            latestBootstrapTime: nowIso,
            mostRecentUpdateTime: nowIso,
          };
          record.events[name] = metadata;
          return metadata;
        }),
    );
  }

  /**
   * The event's serialized state, or `undefined` when there is none or the registry is unreadable; the same
   * degradation as {@link getLatestEventDate}, so a sync that cannot read its state loads from scratch.
   */
  public async getEventState(name: string, workspaceId?: string): Promise<string | undefined> {
    const wsId = this.normalizeWorkspaceId(workspaceId);
    try {
      return await this.withDb(async (db: IDBDatabase): Promise<string | undefined> => {
        const record = await readWorkspaceRecord(db, wsId);
        return record.events[name]?.state;
      });
    } catch (e) {
      log.warn("[BOOTSTRAP][REGISTRY] getEventState failed", { name, workspaceId: wsId, error: e });
      return undefined;
    }
  }

  /** Stores the event's serialized state, keeping its other fields, and stamps the update time. */
  public async setEventState(name: string, state: string, workspaceId?: string): Promise<void> {
    const nowIso = new Date().toISOString();
    await this.withDb(
      async (db: IDBDatabase): Promise<void> =>
        await updateWorkspaceRecord(db, this.normalizeWorkspaceId(workspaceId), (record) => {
          const existing = record.events[name];
          record.events[name] = {
            ...existing,
            name,
            initialBootstrapTime: existing?.initialBootstrapTime ?? nowIso,
            mostRecentUpdateTime: nowIso,
            state,
          };
        }),
    );
  }

  /**
   * Removes every event of the workspace whose name starts with `prefix` and is not in `keep`.
   *
   * A sync that keys its state by a changing set, such as the feed-item warmup's batches, calls this with the keys
   * it is about to use, so the record does not grow by every key an earlier boot minted.
   */
  public async pruneEventStates(prefix: string, keep: ReadonlySet<string>, workspaceId?: string): Promise<void> {
    await this.withDb(
      async (db: IDBDatabase): Promise<void> =>
        await updateWorkspaceRecord(db, this.normalizeWorkspaceId(workspaceId), (record) => {
          for (const name of Object.keys(record.events)) {
            if (name.startsWith(prefix) && !keep.has(name)) delete record.events[name];
          }
        }),
    );
  }

  public async hasBootstrapForWorkspace(workspaceId?: string): Promise<boolean> {
    try {
      return await this.withDb(async (db: IDBDatabase): Promise<boolean> => {
        const record = await readWorkspaceRecord(db, this.normalizeWorkspaceId(workspaceId));
        return Boolean(record.events["bootstrap"]);
      });
    } catch (_e) {
      return false;
    }
  }

  public async clearWorkspace(workspaceId?: string): Promise<void> {
    const wsId = this.normalizeWorkspaceId(workspaceId);
    recordCache.delete(wsId);
    await this.withDb(async (db: IDBDatabase): Promise<void> => {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(IDB_STORE_NAME, "readwrite");
        const store = tx.objectStore(IDB_STORE_NAME);
        const del = store.delete(wsId);
        del.onsuccess = () => resolve();
        del.onerror = () => reject(del.error);
      });
    });
  }

  /**
   * The workspace's cursor state as last read/written this session, captured synchronously. The
   * snapshot persister stores this alongside the rows so a warm boot deltas from the snapshot's
   * timeline even when the registry on disk had advanced past the last persisted rows.
   */
  public getCachedWorkspaceEvents(workspaceId?: string): Record<string, BootstrapEventMetadata> | null {
    return recordCache.get(this.normalizeWorkspaceId(workspaceId))?.events ?? null;
  }

  /** Overwrites the workspace's registry record with snapshot-captured cursor state. */
  public async restoreWorkspace(
    workspaceId: string | undefined,
    events: Record<string, BootstrapEventMetadata>,
  ): Promise<void> {
    await this.withDb(async (db: IDBDatabase): Promise<void> => {
      await writeWorkspaceRecord(db, {
        workspaceId: this.normalizeWorkspaceId(workspaceId),
        events,
        version: IDB_VERSION,
      });
    });
  }

  /**
   * Deletes the registry database. Bounded rather than best-effort: another tab's connection can
   * hold the delete open, and the caller (hardReboot) must reach its reload either way.
   *
   * @returns Whether the delete landed, or is still queued behind another tab.
   */
  public async clearAll(): Promise<DeleteOutcome> {
    return await deleteDatabaseBounded(IDB_DB_NAME);
  }

  private normalizeWorkspaceId(explicitWorkspaceId?: string): string {
    return explicitWorkspaceId ?? activeWorkspaceId ?? "unknown";
  }

  private async withDb<T>(callback: (db: IDBDatabase) => Promise<T>): Promise<T> {
    const db = await openRegistryDb();
    const result = await callback(db);
    db.close();
    return result;
  }
}
